import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Compass, Lock, Mic, Square, Loader2 } from "lucide-react";
import { useDictation } from "@/hooks/useDictation";
import { useI18n } from "@/context/I18nContext";

const CONF = {
  high: { key: "finder.strong", cls: "bg-accent text-accent-foreground" },
  medium: { key: "finder.likely", cls: "bg-primary text-primary-foreground" },
  low: { key: "finder.possible", cls: "bg-secondary text-secondary-foreground border border-border" },
};

export default function FormFinder() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const EXAMPLES = [t("finder.ex1"), t("finder.ex2"), t("finder.ex3"), t("finder.ex4")];
  const { recording, transcribing, toggle } = useDictation((text) => setDesc((p) => (p ? p.trim() + " " : "") + text));

  const run = async () => {
    if (!user) { navigate("/login"); return; }
    if (desc.trim().length < 5) { toast.error(t("finder.moreDetail")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/forms/recommend", { description: desc });
      setResults(data.recommendations || []);
      if (!data.recommendations?.length) toast.message(data.message || t("finder.noClose"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("finder.findFail"));
    } finally {
      setBusy(false);
    }
  };

  const openForm = (number) => {
    sessionStorage.setItem("finderDesc", desc);
    navigate(`/forms/${number}`);
  };

  const startDraft = async (number) => {
    if (!user) { navigate("/login"); return; }
    try {
      const { data } = await api.post("/submissions", { form_number: number, field_values: {} });
      sessionStorage.setItem("finderDesc", desc);
      navigate(`/forms/${number}?submission=${data.id}`);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.message(t("finder.trialPrompt"));
        navigate("/pricing");
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || t("finder.draftFail"));
      }
    }
  };

  return (
    <div>
      <Navbar />
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-10">
        <p className="overline text-accent mb-4 flex items-center gap-2"><Compass size={14} /> {t("finder.eyebrow")}</p>
        <h1 className="font-serif font-bold text-5xl sm:text-6xl tracking-tighter mb-4">{t("finder.title")}</h1>
        <p className="text-lg text-muted-foreground mb-8 max-w-2xl">
          {t("finder.subtitle")}
        </p>

        <div className="grid-card p-2">
          <Textarea data-testid="finder-input" value={desc} onChange={(e) => setDesc(e.target.value)} rows={4}
            className="rounded-sm border-0 focus-visible:ring-0 resize-none text-base"
            placeholder={t("finder.placeholder")} />
          <div className="flex items-center justify-between p-2">
            <span className="text-xs text-muted-foreground hidden sm:block">
              {recording ? <span className="text-accent font-medium">{t("finder.listening")}</span>
                : transcribing ? t("finder.transcribing")
                : t("finder.hint")}
            </span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={toggle} disabled={transcribing} data-testid="finder-mic-btn"
                title={recording ? "Stop dictation" : "Speak your situation"}
                className={`h-10 w-10 grid place-items-center rounded-full transition-colors shrink-0 ${recording ? "bg-accent text-accent-foreground animate-pulse" : "bg-secondary hover:bg-primary hover:text-primary-foreground"}`}>
                {transcribing ? <Loader2 size={17} className="animate-spin" /> : recording ? <Square size={16} /> : <Mic size={17} />}
              </button>
              <Button onClick={run} disabled={busy} data-testid="finder-run-btn" className="rounded-sm gap-2 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
                <Sparkles size={16} /> {busy ? t("finder.searching") : t("finder.findBtn")}
              </Button>
            </div>
          </div>
        </div>

        {!user && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground mt-4">
            <Lock size={14} /> <Link to="/login" className="text-accent hover:underline">{t("finder.loginLink")}</Link>{t("finder.loginSuffix")}
          </p>
        )}

        {!results && (
          <div className="mt-8">
            <p className="overline font-bold text-foreground mb-3">{t("finder.tryExample")}</p>
            <div className="flex flex-wrap gap-2">
              {EXAMPLES.map((e) => (
                <button key={e} onClick={() => setDesc(e)} className="text-left text-sm border border-border px-4 py-2 rounded-sm hover:border-primary transition-colors">
                  {e}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {results && (
        <section className="max-w-4xl mx-auto px-6 pb-24">
          {results.length === 0 ? (
            <div className="grid-card p-10 text-center">
              <p className="text-muted-foreground mb-4">{t("finder.noMatch")}</p>
              <Link to="/forms"><Button variant="outline" className="rounded-sm">{t("finder.browseLibrary")}</Button></Link>
            </div>
          ) : (
            <>
              <h2 className="font-serif font-bold text-3xl mb-6">{t("finder.recommended")}</h2>
              <div className="space-y-4">
                {results.map((r, i) => (
                  <div key={r.number} data-testid={`finder-result-${r.number}`}
                    className="grid-card p-6 flex flex-col sm:flex-row sm:items-center gap-4 animate-fade-up" style={{ animationDelay: `${i * 0.06}s` }}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge variant="outline" className="rounded-sm font-mono text-xs">{r.number}</Badge>
                        {r.confidence && (
                          <span data-testid={`finder-confidence-${r.number}`}
                            className={`text-[0.6rem] uppercase tracking-wider px-2 py-0.5 rounded-sm font-semibold ${CONF[r.confidence]?.cls || CONF.medium.cls}`}>
                            {t(CONF[r.confidence]?.key || "finder.possible")}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground uppercase tracking-wider">{r.category}</span>
                      </div>
                      <h3 className="font-serif font-bold text-xl leading-tight mb-1">{r.title}</h3>
                      {r.reason && <p className="text-sm text-accent flex items-start gap-1"><Sparkles size={13} className="mt-0.5 shrink-0" /> {r.reason}</p>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button onClick={() => openForm(r.number)} data-testid={`finder-open-${r.number}`} variant="outline"
                        className="rounded-sm hover:bg-secondary">{t("finder.preview")}</Button>
                      <Button onClick={() => startDraft(r.number)} data-testid={`finder-draft-${r.number}`}
                        className="rounded-sm gap-2 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
                        {t("finder.startDraft")} <ArrowRight size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      )}
      <Footer />
    </div>
  );
}
